import { ExportFormat, ExportMenu } from "@asd/customer-portal-ui";
import DownloadIcon from "@mui/icons-material/Download";
import { useTheme } from "@mui/material";
import type { GridColDef } from "@mui/x-data-grid/models";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";

import { USDollar } from "~/utils/stringConverters";
import { downloadCsv, downloadXlsx, inferXlsxCell, XlsxCell } from "~/utils/tableExport";
import { getUnitUsageColor } from "~/utils/unitUsageHelpers";
import {
  getColumnGroupingModel,
  getColumnValue,
  getDivisionVarianceColor,
  getRiskDisplay,
  getTrendDisplay,
  getVarianceColor,
} from "./helpers";
import {
  COLUMN_WIDTHS_MM,
  cssColorToRgb,
  FLEXIBLE_MIN_WIDTHS_MM,
  PDF_HEADER_OVERRIDES,
  renderIconTextDataUrl,
  renderOutlinedLabelDataUrl,
  resolveThemeColor,
} from "./pdfExportHelpers";

export type ReportsExportProps = {
  rows: any[];
  columns: GridColDef[];
  reportLabel: string;
  month: string;
  disabled: boolean;
};

const DIVISION_DOLLAR_FIELDS = ["monthBudget", "monthExpense", "monthAee", "fyBudget", "fyExpense", "fyAee"];
const DIVISION_PERCENT_FIELDS = ["monthPercentBudget", "fyPercentBudget"];
const DIVISION_NUMBER_FIELDS = ["monthVariance", "fyVariance"];
const DIVISION_NUMERIC_FIELDS = [...DIVISION_DOLLAR_FIELDS, ...DIVISION_PERCENT_FIELDS, ...DIVISION_NUMBER_FIELDS];

// Serializes a row to plain string values for each column, replacing custom renderers
export const serializeRow = (row: any, columns: GridColDef[]): string[] =>
  columns.map((col) => {
    const value = getColumnValue(columns, col.field, row);

    if (col.field === "snapshot") {
      const used = ((row.claimUnits / row.allocatedUnits) * 100).toFixed(2);
      const expected = ((row.expectedUnits / row.allocatedUnits) * 100).toFixed(2);
      return `${used}% used / ${expected}% expected`;
    }

    if (["allocatedUnits", "claimUnits"].includes(col.field)) {
      return `${value} ${row.unitLabel}${value !== 1 ? "s" : ""}`;
    }

    if (col.field === "expectedUnits") {
      return `${value?.toFixed(2)} ${row.unitLabel}${value !== 1 ? "s" : ""}`;
    }

    if (col.field === "variancePercentage") return `${value?.toFixed(2)}%`;

    if (DIVISION_DOLLAR_FIELDS.includes(col.field)) return USDollar.format(value ?? 0);

    if (DIVISION_PERCENT_FIELDS.includes(col.field)) return `${(value ?? 0).toFixed(2)}%`;

    if (DIVISION_NUMBER_FIELDS.includes(col.field)) return (value ?? 0).toFixed(2);

    return value != null ? String(value) : "";
  });

const ReportsExportToolbar = ({ rows, columns, reportLabel, month, disabled }: ReportsExportProps) => {
  const theme = useTheme();

  const headers = columns.map((col) => {
    const label = col.headerName ?? col.field;
    return PDF_HEADER_OVERRIDES[label] ?? label;
  });

  // Division report has grouped column headers (Monthly / Plan Year-To-Date) with repeated column names
  const isDivision = columns.some((col) => col.field === "monthBudget");
  const columnGroups: { groupId: string; children: { field: string }[] }[] = isDivision ? (getColumnGroupingModel("division") ?? []) : [];
  const groupByField = new Map<string, string>();
  columnGroups.forEach((group) => group.children.forEach((child) => groupByField.set(child.field, group.groupId)));
  // CSV/XLSX have a single header row, so repeated names get the group as a prefix
  const flatHeaders = isDivision
    ? columns.map((col, index) => {
        const group = groupByField.get(col.field);
        return group && group !== "Member/Participant" ? `${group} ${headers[index]}` : headers[index];
      })
    : headers;
  const isPercentField = (field: string) => DIVISION_PERCENT_FIELDS.includes(field);
  const filename = `${reportLabel}-${month}`;
  const snapshotColIndex = columns.findIndex((col) => col.field === "snapshot");
  const trendColIndex = columns.findIndex((col) => col.field === "trendDirection");
  const riskColIndex = columns.findIndex((col) => col.field === "riskLevel");
  const varianceColIndex = columns.findIndex((col) => col.field === "variancePercentage");
  const varianceColored = varianceColIndex !== -1 && (columns[varianceColIndex] as any).colorInExport === true;

  const handleExportPdf = () => {
    const doc = new jsPDF({ orientation: "landscape" });
    const headerColor = cssColorToRgb(theme.palette.primary.main);

    doc.setFontSize(13);
    doc.text(`${reportLabel} — ${month}`, 14, 14);

    const body = rows.map((row) => serializeRow(row, columns));

    // Columns whose body text gets blanked and replaced with a canvas-rendered image in didDrawCell
    const iconTextColumns = [
      { index: trendColIndex, hasIcon: true },
      { index: riskColIndex, hasIcon: true },
      { index: varianceColIndex, hasIcon: false },
    ].filter((c) => c.index !== -1);

    doc.setFontSize(8);
    const minWidths: Record<number, number> = {};

    if (snapshotColIndex !== -1) {
      minWidths[snapshotColIndex] =
        Math.max(
          ...rows.map((row) =>
            doc.getTextWidth(`${row.claimUnits} of ${row.allocatedUnits} ${row.unitLabel}${row.claimUnits !== 1 ? "s" : ""}`),
          ),
          doc.getTextWidth(headers[snapshotColIndex]),
        ) + 8;
    }

    iconTextColumns.forEach(({ index, hasIcon }) => {
      const labels = body.map((cells) => cells[index]);
      minWidths[index] = Math.max(...labels.map((l) => doc.getTextWidth(l)), doc.getTextWidth(headers[index])) + 8 + (hasIcon ? 8 : 0);
    });
    doc.setFontSize(9);

    const measuredTextColumns = ["allocatedUnits", "claimUnits", "expectedUnits", "startDate", "stopDate", "monthsFlagged"]
      .map((field) => columns.findIndex((c) => c.field === field))
      .filter((index) => index !== -1);

    doc.setFontSize(8);
    measuredTextColumns.forEach((index) => {
      const cellValues = body.map((cells) => cells[index]);
      minWidths[index] = Math.max(...cellValues.map((v) => doc.getTextWidth(v)), doc.getTextWidth(headers[index])) + 6;
    });
    doc.setFontSize(9);

    if (snapshotColIndex !== -1) body.forEach((cells) => (cells[snapshotColIndex] = ""));
    iconTextColumns.forEach(({ index }) => body.forEach((cells) => (cells[index] = "")));

    const columnStyles: Record<number, { cellWidth?: number; minCellWidth?: number; halign?: "right" }> = {};
    columns.forEach((col, index) => {
      if (isDivision && DIVISION_NUMERIC_FIELDS.includes(col.field)) {
        columnStyles[index] = { halign: "right" };
      } else if (minWidths[index] != null) {
        columnStyles[index] = { cellWidth: minWidths[index] };
      } else if (COLUMN_WIDTHS_MM[col.field] != null) {
        columnStyles[index] = { cellWidth: COLUMN_WIDTHS_MM[col.field] };
      } else if (FLEXIBLE_MIN_WIDTHS_MM[col.field] != null) {
        columnStyles[index] = { minCellWidth: FLEXIBLE_MIN_WIDTHS_MM[col.field] };
      }
    });

    const pageWidth = doc.internal.pageSize.getWidth();
    const marginTotal = 10; // 5mm left + 5mm right, matching the `margin` option below
    const availableWidth = pageWidth - marginTotal;

    const totalRequestedWidth = Object.values(columnStyles).reduce((sum, style) => sum + (style.cellWidth ?? style.minCellWidth ?? 0), 0);

    if (totalRequestedWidth > availableWidth) {
      const scale = availableWidth / totalRequestedWidth;
      Object.keys(columnStyles).forEach((key) => {
        const style = columnStyles[Number(key)];
        if (style.cellWidth != null) {
          style.cellWidth *= scale;
        } else if (style.minCellWidth != null) {
          style.minCellWidth *= scale;
        }
      });
    }

    autoTable(doc, {
      startY: 20,
      head: columnGroups.length
        ? [
            columnGroups.map((group) => ({
              content: group.groupId,
              colSpan: group.children.length,
              styles: { halign: "center" as const },
            })),
            headers,
          ]
        : [headers],
      body,
      didParseCell: (data) => {
        if (!isDivision || data.section !== "body") return;
        const field = columns[data.column.index]?.field;
        if (!isPercentField(field)) return;
        const color = getDivisionVarianceColor(getColumnValue(columns, field, rows[data.row.index]));
        if (color) data.cell.styles.textColor = cssColorToRgb(resolveThemeColor(theme, color));
      },
      margin: { left: 5, right: 5 },
      tableWidth: "auto",
      styles: {
        fontSize: 8,
        cellPadding: 1.5,
        minCellHeight: 0,
        valign: "middle",
        overflow: "linebreak",
        // Thin light gray cell borders for the dense division table
        ...(isDivision ? { lineWidth: 0.1, lineColor: [210, 210, 210] as [number, number, number] } : {}),
      },
      headStyles: {
        fillColor: headerColor,
        cellPadding: 1.5,
        valign: "middle",
      },
      columnStyles,
      didDrawCell: (data) => {
        if (data.section !== "body") return;

        const row = rows[data.row.index];
        if (!row) return;

        if (data.column.index === snapshotColIndex) {
          const padding = 1;
          const radius = 1.5;

          const x = data.cell.x + padding;
          const y = data.cell.y + padding;
          const width = data.cell.width - padding * 2;
          const height = data.cell.height - padding * 2;

          const usedPercentage = row.allocatedUnits ? (row.claimUnits / row.allocatedUnits) * 100 : 0;
          const expectedPercentage = row.allocatedUnits ? (row.expectedUnits / row.allocatedUnits) * 100 : 0;

          const colorResult = getUnitUsageColor(usedPercentage, theme);
          const colorValue = typeof colorResult === "function" ? colorResult(theme) : colorResult;
          const [r, g, b] = cssColorToRgb(colorValue);

          const usedWidth = (Math.min(Math.max(usedPercentage, 0), 100) / 100) * width;

          // Clip everything drawn next to the rounded track shape, then fill a plain rect —
          // the clip gives it rounded corners regardless of how much of the width it covers
          doc.saveGraphicsState();
          doc.roundedRect(x, y, width, height, radius, radius, null);
          doc.clip();
          doc.discardPath();

          doc.setFillColor(r, g, b);
          doc.rect(x, y, usedWidth, height, "F");

          doc.restoreGraphicsState();

          // Track outline, drawn after restoring so it isn't affected by the clip
          doc.setDrawColor(220, 220, 220);
          doc.setLineWidth(0.2);
          doc.roundedRect(x, y, width, height, radius, radius, "S");

          const expectedX = x + (Math.min(Math.max(expectedPercentage, 0), 100) / 100) * width;
          doc.setDrawColor(255, 0, 0);
          doc.setLineWidth(0.5);
          doc.line(expectedX, y, expectedX, y + height);

          const label = `${row.claimUnits} of ${row.allocatedUnits} ${row.unitLabel}${row.claimUnits !== 1 ? "s" : ""}`;

          const fontSizePt = 8;
          const { dataUrl, width: pxWidth, height: pxHeight } = renderOutlinedLabelDataUrl(label, fontSizePt);

          const targetHeightMm = fontSizePt * 0.3528 * 1.6;
          const targetWidthMm = targetHeightMm * (pxWidth / pxHeight);

          doc.addImage(dataUrl, "PNG", x + 2, y + height / 2 - targetHeightMm / 2, targetWidthMm, targetHeightMm);
          return;
        }

        let display: { label: string; colorToken?: string; iconKey?: string | null } | null = null;

        if (data.column.index === trendColIndex) {
          const { color, icon } = getTrendDisplay(row.trendDirection);
          display = { label: String(row.trendDirection ?? ""), colorToken: color, iconKey: icon };
        } else if (data.column.index === riskColIndex) {
          const { color, icon } = getRiskDisplay(row.riskLevel);
          display = { label: String(row.riskLevel ?? ""), colorToken: color, iconKey: icon };
        } else if (data.column.index === varianceColIndex) {
          const value = getColumnValue(columns, "variancePercentage", row);
          display = { label: `${value?.toFixed(2)}%`, colorToken: varianceColored ? getVarianceColor(value) : undefined, iconKey: null };
        }

        if (!display) return;

        const color = resolveThemeColor(theme, display.colorToken);
        const fontSizePt = 8;
        const { dataUrl, width: pxWidth, height: pxHeight } = renderIconTextDataUrl(display.label, color, display.iconKey, fontSizePt);

        const targetHeightMm = fontSizePt * 0.3528 * 1.6;
        const targetWidthMm = targetHeightMm * (pxWidth / pxHeight);

        doc.addImage(
          dataUrl,
          "PNG",
          data.cell.x + 2,
          data.cell.y + data.cell.height / 2 - targetHeightMm / 2,
          targetWidthMm,
          targetHeightMm,
        );
      },
    });

    doc.save(`${filename}.pdf`);
  };

  const handleExportCsv = () => {
    const escape = (v: string) => `"${v.replace(/"/g, '""')}"`;
    const lines = [flatHeaders.map(escape).join(","), ...rows.map((row) => serializeRow(row, columns).map(escape).join(","))];
    downloadCsv(lines.join("\n"), filename);
  };

  // Same values as the CSV, but stored as real numbers/dates with the table's colors where it has them
  const handleExportXlsx = () => {
    const xlsxRows = rows.map((row) =>
      serializeRow(row, columns).map((text, index): XlsxCell => {
        const { field } = columns[index];
        const unitSuffix = (value: number) => `" ${row.unitLabel}${value !== 1 ? "s" : ""}"`;

        if (["allocatedUnits", "claimUnits", "expectedUnits"].includes(field)) {
          const value = getColumnValue(columns, field, row);
          if (typeof value === "number") {
            return { value, numFmt: `${field === "expectedUnits" ? "0.00" : "General"}${unitSuffix(value)}` };
          }
        }

        if (index === varianceColIndex) {
          const value = getColumnValue(columns, field, row);
          if (typeof value === "number") {
            return {
              value: value / 100,
              numFmt: "0.00%",
              color: varianceColored ? resolveThemeColor(theme, getVarianceColor(value)) : undefined,
            };
          }
        }

        if (isDivision && isPercentField(field)) {
          const color = getDivisionVarianceColor(getColumnValue(columns, field, row));
          return { ...inferXlsxCell(text), color: color ? resolveThemeColor(theme, color) : undefined };
        }

        if (isDivision && DIVISION_NUMBER_FIELDS.includes(field)) {
          return { value: Number(text), numFmt: "0.00" };
        }

        if (index === trendColIndex) {
          return { value: text, color: resolveThemeColor(theme, getTrendDisplay(row.trendDirection).color) };
        }

        if (index === riskColIndex) {
          return { value: text, color: resolveThemeColor(theme, getRiskDisplay(row.riskLevel).color) };
        }

        return inferXlsxCell(text);
      }),
    );

    downloadXlsx({ filename, headerColor: theme.palette.primary.main, headers: flatHeaders, rows: xlsxRows, sheetName: reportLabel });
  };

  const handleExport = (format: ExportFormat) => {
    if (format === "pdf") handleExportPdf();
    else if (format === "xlsx") handleExportXlsx();
    else handleExportCsv();
  };

  return (
    <ExportMenu disabled={disabled} formats={["pdf", "csv", "xlsx"]} icon={<DownloadIcon fontSize="small" />} onExport={handleExport} />
  );
};

export default ReportsExportToolbar;
